import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import MainLayout from '../../../layouts/MainLayout';
import '../styles/MCQPractice.css';
import { useSelector } from 'react-redux';
import Loader from '../../../components/utils/Loader';
import Tooltip from '../../../components/utils/Tooltip';

const PracticeMCQ = () => {
    const authState = useSelector(state => state.authReducer);
    const [fetchData, { loading }] = useFetch();
    const navigate = useNavigate();
    const [selectedSetId, setSelectedSetId] = useState('');
    const [setMasterList, setSetMasterList] = useState([]);
    const [practicedSetList, setPracticedSetList] = useState([]);
    const [selectedPracticeSetId, setSelectedPracticeSetId] = useState('');

    const [practicedMaster, setPracticedMaster] = useState([]);
    const [isRepeated, setIsRepeated] = useState(0);

    //SetMasterList
    const fetchSetMasters = useCallback(() => {
        const config = {
            url: "/setmaster/sets",
            method: "post",
            headers: { Authorization: authState.token },
            data: { "isT5w": 1, "setType": "Practice", "setQuestionType": "MCQ" },
        };

        fetchData(config, { showSuccessToast: false })
            .then(response => {
                // console.log("Response:", response);
                if (response && Array.isArray(response.setmasterlist)) {
                    setSetMasterList(response.setmasterlist);
                } else {
                    console.error("Invalid response format: Set master list not found in response");
                }
            })
            .catch(error => {
                console.error("Error fetching set masters:", error);
            });
    }, [authState.token, fetchData]);

    useEffect(() => {
        if (!authState.isLoggedIn) return;
        fetchSetMasters();
    }, [authState.isLoggedIn, fetchSetMasters]);

    //MyPracticedSetList
    const fetchPracticedSetList = useCallback(() => {
        const config = {
            url: "/mypracticedsets",
            method: "get",
            headers: { Authorization: authState.token, "isT5w": 1, "setQuestionType": "MCQ" }
        };

        fetchData(config, { showSuccessToast: false })
            .then(response => {
                // console.log("Practiced Set List Response:", response);
                if (response && Array.isArray(response.practicedsetlist)) {
                    setPracticedSetList(response.practicedsetlist);
                } else {
                    console.error("Invalid response format: Practice set list not found in response");
                }
            })
            .catch(error => {
                console.error("Error fetching practice list:", error);
            });
    }, [authState.token, fetchData]);

    useEffect(() => {
        fetchPracticedSetList();
    }, [fetchPracticedSetList]);

    const sortedSetMasterList = [...setMasterList].sort((a, b) => a.setDisplaySn - b.setDisplaySn);
    const sortedPracticedSetList = [...practicedSetList].sort((a, b) => new Date(b.datePracticed) - new Date(a.datePracticed));

    useEffect(() => {
        let newPracticedMaster = [];
        sortedPracticedSetList.forEach(practicedSet => {
            const matchingSetMaster = sortedSetMasterList.find(setMaster => setMaster._id === practicedSet.setId);
            if (matchingSetMaster) {
                newPracticedMaster.push(matchingSetMaster);
            }
        });
        setPracticedMaster(newPracticedMaster);
    }, [sortedSetMasterList, sortedPracticedSetList]);


    const handleSetMasterView = (setId) => {
        setSelectedSetId(prevId => (prevId === setId ? null : setId));
    };

    const handlePracticeSetView = (setId) => {
        setSelectedPracticeSetId(prevId => (prevId === setId ? null : setId));
    };

    const handlePracticeClick = (setId, setTitle, seriesId, seriesTitle, examId, examTitle, examType, timeLimitInMin, isRepeated) => {
        navigate('/mcq-test', { state: { setId: setId, setTitle: setTitle, seriesId: seriesId, seriesTitle: seriesTitle, examId: examId, examTitle: examTitle, examType: examType, timeLimitInMin: timeLimitInMin, isRepeatPractice: isRepeated } });
    };

    function convertTimeLimit(minutes) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        const formattedTimeLimit = `${hours}:${remainingMinutes} (Hr:Min)`;
        return formattedTimeLimit;
    }

    function convertTimeTaken(milliseconds) {
        const hours = Math.floor(milliseconds / 3600000);
        const remainingMinutes = Math.floor((milliseconds % 3600000) / 60000);
        const remainingSeconds = Math.floor((milliseconds % 60000) / 1000);
        const remainingMilliseconds = milliseconds % 1000;
        const formattedTime = `${hours}:${remainingMinutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds
                .toString()
                .padStart(2, "0")}.${remainingMilliseconds
                    .toString()
                    .padStart(3, "0")} (Hr:Min:Sec)`;
        return formattedTime;
    }

    const formatDateTime = (date) => {
        const options = {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        };
        const jsDate = new Date(date);
        const day = jsDate.getDate().toString().padStart(2, '0');
        const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
        const year = jsDate.getFullYear();
        const hours = jsDate.getHours().toString().padStart(2, '0');
        const minutes = jsDate.getMinutes().toString().padStart(2, '0');
        const seconds = jsDate.getSeconds().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    };


    return (
        <div className="min-h-screen bg-black py-2">
            <div className='border-b border-blue-400 mb-2'>
                <h2 className="text-m lg:text-lg font-extrabold text-center text-yellow-200 p-2">
                📖 Think5Whys Books Practice - MCQ Type Questions
                </h2>
            </div>
            <div className="list-container p-2">
                {loading ? (
                    <Loader />
                ) : (
                    <div className="practicelist-container ml-4">
                        <h2 className="ml-2 text-m lg:text-lg text-white bg-blue-600 rounded p-2">Think5Whys Books MCQ Practice Set List</h2>
                        <div>
                            {setMasterList && setMasterList.length === 0 ? (
                                <div className='text-m lg:text-lg text-orange-600'>
                                    No records found!
                                </div>
                            ) : (
                                sortedSetMasterList.map((setMaster, index) => (
                                    <div key={setMaster._id} className="bg-gray-600 my-4 p-4 text-white rounded-md shadow-md">
                                        <div className="flex flex-col lg:flex-row lg:justify-between items-left gap-2">
                                        <span
                                            className={`text-left font-medium px-2 py-1 rounded ${
                                            practicedMaster.some(practiced => practiced._id === setMaster._id) ? 'text-yellow-500 bg-gray-700' : ''
                                            }`}
                                        >
                                            {index + 1}. {setMaster.setTitle}
                                        </span>

                                        <div className="flex flex-row flex-wrap sm:flex-nowrap items-center gap-2 mt-2 sm:mt-0">
                                            <Tooltip text="View details" position="top">
                                            <span
                                                className="px-2 py-1 text-yellow-200 cursor-pointer rounded hover:bg-gray-700"
                                                onClick={() => handleSetMasterView(setMaster._id)}
                                            >
                                                <i className="fa-solid fa-eye"></i>
                                            </span>
                                            </Tooltip>
                                            <button
                                            className={`w-32 lg:w-40 px-2 py-1 rounded text-white ${
                                                practicedMaster.find(item => item._id === setMaster._id) ? 'bg-blue-500' : 'bg-green-500'
                                            }`}
                                            onClick={() =>
                                                handlePracticeClick(
                                                setMaster._id,
                                                setMaster.setTitle,
                                                setMaster.seriesId,
                                                setMaster.seriesTitle,
                                                setMaster.examId,
                                                setMaster.examTitle,
                                                setMaster.examType,
                                                setMaster.timeLimitInMin,
                                                practicedMaster.find(item => item._id === setMaster._id) ? 1 : 0
                                                )
                                            }
                                            >
                                            {practicedMaster.find(item => item._id === setMaster._id) ? 'Repeat Practice' : 'Practice Now'}
                                            </button>
                                        </div>
                                        </div>

                                        {/* Description */}
                                        <div className="text-yellow-200 text-left text-sm lg:text-m mt-2 ml-0 sm:ml-4">{setMaster.setDesc}</div>

                                        {/* Expanded Details */}
                                        {selectedSetId === setMaster._id && (
                                        <div className="bg-black text-white mt-2 p-2 rounded-md space-y-1">
                                            <h2 className="text-yellow-200 font-semibold">{setMaster.setTitle}</h2>
                                            <p><strong>Total Questions:</strong> {setMaster.setTotalQuestions}</p>
                                            <p><strong>Time Limit (Minutes):</strong> {setMaster.timeLimitInMin}</p>
                                            <p><strong>Correct Answer Points:</strong> {setMaster.correctAnswerPoints}</p>
                                            <p><strong>Wrong Answer Points:</strong> {setMaster.WrongAnswerPoints}</p>
                                            <p><strong>Reference Book:</strong> {setMaster.setRefBookName}</p>
                                            <p><strong>Author:</strong> {setMaster.setAuthor}</p>
                                            <p><strong>Test Series:</strong> {setMaster.seriesTitle}</p>
                                            <p><strong>Target Exam:</strong> {setMaster.examTitle}</p>
                                            <p><strong>Exam Type:</strong> {setMaster.examType}</p>
                                        </div>
                                        )}
                                    </div>
                                 ))
                            )}
                        </div>
                        <h2 className="ml-2 text-m lg:text-lg text-white bg-green-600 rounded p-2">My Practice List</h2>
                        <div>
                            {practicedSetList && practicedSetList.length === 0 ? (
                                <div className='mt-4 text-m lg:text-lg text-orange-600'>
                                    No records found!
                                </div>
                            ) : (
                                sortedPracticedSetList.map((practicedSet, index) => (
                                    <div key={practicedSet._id} className="bg-gray-600 my-4 p-4 text-white rounded-md shadow-md">
                                        <div className='flex'>
                                            <span className='font-medium'>{index + 1}. {practicedSet.practiceTitle},  Date: {formatDateTime(practicedSet.datePracticed)}</span>
                                            <Tooltip text={"View details"} position={"top"}>
                                                <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handlePracticeSetView(practicedSet._id)}>
                                                    <i className="fa-solid fa-eye"></i>
                                                </span>
                                            </Tooltip>
                                        </div>
                                        {selectedPracticeSetId === practicedSet._id && (
                                            <div className='bg-gray-200 text-black mt-4 p-4 rounded-md'>
                                                <h2 className='text-yellow-200 text-lg md:text-xl font-bold'>{practicedSet.practiceTitle}</h2>
                                                <p><strong>Date Practiced:</strong> {new Date(practicedSet.datePracticed).toLocaleDateString()}</p>
                                                <p><strong>Is Repeated:</strong> {practicedSet.isRepeatPractice === 1 ? 'Yes' : 'No'}</p>
                                                <p><strong>Time Limit:</strong> {convertTimeLimit(practicedSet.timeLimit)}</p>
                                                <p><strong>Time Taken:</strong> {convertTimeTaken(practicedSet.timeTaken)}</p>
                                                <p><strong>Number of Questions:</strong> {practicedSet.numberOfQuestions}</p>
                                                <p><strong>Correct Answers:</strong> {practicedSet.correctAnswers}</p>
                                                <p><strong>Full Marks:</strong> {practicedSet.fullMarks}</p>
                                                <p><strong>Marks Obtained:</strong> {practicedSet.marksObtained}</p>
                                                <p><strong>Percentage:</strong> {practicedSet.percentage}%</p>
                                                <div className="progress-container">
                                                    <div className="progress-bar">
                                                        <div className="progress" style={{ width: `${practicedSet.percentage}%` }}>
                                                            {`${practicedSet.percentage}%`}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="pl-4">
                                                    <h3 className="text-lg font-bold text-yellow-200 mt-4">Answers:</h3>
                                                    {practicedSet.answers.map((answer, index) => (
                                                        <div key={index} className="mt-2">
                                                            <p><strong>Question {index + 1}:</strong> {answer.questionTitle}</p>
                                                            <p className={answer.marksObtained <= 0 ? 'text-red-500' : ''}><strong>My Answer:</strong> {answer.userMCQAnswer}</p>
                                                            <p className={answer.marksObtained <= 0 ? 'text-red-500' : ''}><strong>Marks Obtained:</strong> {answer.marksObtained}</p>
                                                            <p><strong>Marks for Correct Answer:</strong> {answer.correctAnswerPoints}</p>
                                                            {answer.isMCQCorrect === 0 && <p><strong>Marks for Wrong Answer:</strong> {answer.wrongAnswerPoints}</p>}
                                                            {answer.remarks && <p><strong>Remarks:</strong> {answer.remarks}</p>}
                                                            {answer.userShortAnswer && <p><strong>My Short Answer:</strong> {answer.userShortAnswer}</p>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
            <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
                <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
                <div className="flex flex-wrap gap-4 text-sm">
                    <Link to="/about-us" className="hover:text-teal-300">About</Link>
                    <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
                    <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
                    <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
                </div>
                </div>
            </footer>              
        </div>
    );
};

export default PracticeMCQ;
